import { Info as InfoIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import AuditLogDetailDialog from "../components/AuditLogDetailDialog";
import CopyableValue from "../components/CopyableValue";
import MarketGrowthDataTable, {
  MarketGrowthServerPagination,
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import { toSelectOptions } from "../constants/commissionEnums";
import {
  getAuditActionLabel,
  getAuditEntityLabel,
  COMMISSION_AUDIT_ENTITY_LABELS,
} from "../constants/reportEnums";
import { formatApiDate } from "../utils/commissionValidation";

const PAGE_SIZE = 25;

const MarketGrowthAuditLogsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    entityType: "",
    entityId: "",
    actorUserId: "",
    fromDate: null,
    toDate: null,
  });
  const [detailRow, setDetailRow] = useState(null);

  const buildParams = useCallback(() => {
    const params = { page, pageSize: PAGE_SIZE };
    if (filters.entityType) params.entityType = filters.entityType;
    if (filters.entityId) params.entityId = Number(filters.entityId);
    if (filters.actorUserId) params.actorUserId = Number(filters.actorUserId);
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getAuditLogs(buildParams());
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری لاگ حسابرسی ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        setTotalCount(0);
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.totalCount) || 0);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        entityTypeLabel: getAuditEntityLabel(row.entityType),
        actionTypeLabel: getAuditActionLabel(row.actionType),
        actorLabel: row.actorUserId ? String(row.actorUserId) : "—",
      })),
    [items]
  );

  const headCells = useMemo(
    () => [
      { id: "entityTypeLabel", label: "نوع موجودیت", type: "chip" },
      {
        id: "entityId",
        label: "شناسه موجودیت",
        render: (value) => <CopyableValue value={value} />,
      },
      { id: "actionTypeLabel", label: "عملیات", type: "chip" },
      {
        id: "actorLabel",
        label: "کاربر عامل",
        render: (_, row) =>
          row.actorUserId ? <CopyableValue value={row.actorUserId} /> : "—",
      },
      { id: "summary", label: "توضیحات" },
      { id: "changedAt", label: "تاریخ", type: "persiandatetime" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailRow(row),
        color: "primary",
        tooltip: "جزئیات",
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  return (
    <MarketGrowthPageShell
      title="لاگ حسابرسی"
      description="سوابق تغییرات و عملیات ماژول پورسانت."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع موجودیت</InputLabel>
                <Select
                  label="نوع موجودیت"
                  value={filters.entityType}
                  onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(COMMISSION_AUDIT_ENTITY_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="شناسه موجودیت"
                value={filters.entityId}
                onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="کاربر عامل"
                value={filters.actorUserId}
                onChange={(e) => setFilters((f) => ({ ...f, actorUserId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>
                  اعمال فیلتر
                </Button>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={load}>
                  بروزرسانی
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            pageSize={PAGE_SIZE}
            showPaging={false}
            defaultSort={{ orderBy: "changedAt", order: "desc" }}
            tableMinWidth={1000}
          />
          <MarketGrowthServerPagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <AuditLogDetailDialog
        open={Boolean(detailRow)}
        data={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthAuditLogsPage;
